import { useEffect, useState } from 'preact/hooks';
import { Knot, type KnotProps } from './Knot';
import { CtwBot } from './CtwBot';
import { getDeploymentVertical } from '../lib/api';

// Which face does capture wear? The Knot is the FLEET's capture identity;
// the clapperbot is CallToWrap's own (Huan, 2026-08-24 — a deliberate trial,
// "let's try", one revert away). The choice keys on the DEPLOYMENT's
// vertical — the same authority the door's copy uses (packs[0].key via
// GET /auth/entry) — never a tenant's, because the door renders before any
// tenant exists. Every product that is not actor_assist keeps the Knot,
// byte-identical.
//
// The answer is fetched once per page load and cached at module level; until
// it arrives, actor_assist deployments briefly render the Knot. That flash is
// bounded by the /auth/entry round trip the door already waits on, and the
// in-app surfaces mount long after it has resolved.

let cachedVertical: string | null | undefined;
let pending: Promise<string | null> | undefined;

function deploymentVertical(): Promise<string | null> {
  if (cachedVertical !== undefined) return Promise.resolve(cachedVertical);
  if (!pending) {
    pending = getDeploymentVertical()
      .then((v) => {
        cachedVertical = v;
        return v;
      })
      .catch(() => {
        // An unreachable /auth/entry must not take the capture glyph down
        // with it — fall back to the fleet identity and let a later mount
        // retry.
        pending = undefined;
        return null;
      });
  }
  return pending;
}

/** Test seam: force the answer (and forget it with `undefined`). */
export function __setDeploymentVerticalForTest(v: string | null | undefined): void {
  cachedVertical = v;
  pending = undefined;
}

export function CaptureGlyph(props: KnotProps) {
  const [vertical, setVertical] = useState<string | null | undefined>(cachedVertical);
  useEffect(() => {
    if (vertical !== undefined) return;
    let alive = true;
    void deploymentVertical().then((v) => {
      if (alive) setVertical(v);
    });
    return () => {
      alive = false;
    };
  }, [vertical]);

  // ⚑ NOTHING IS DRAWN UNTIL THE DEPLOYMENT HAS ANSWERED. Huan, 2026-08-28:
  // a person on CallToWrap the product "should not see Vowric anywhere" —
  // and `undefined` here means "not asked yet", not "not actor_assist".
  // Falling through to the Knot during that beat drew VOWRIC'S MARK on a
  // CallToWrap sign-in door and then swapped it for Clappy once the fetch
  // landed. A visible flash of the wrong brand is the defect; a mark that
  // arrives a beat late is not. Space is held so nothing jumps.
  if (vertical === undefined) {
    return <span aria-hidden="true" style={{ display: 'inline-block', width: props.size, height: props.size }} />;
  }
  return vertical === 'actor_assist' ? <CtwBot {...props} /> : <Knot {...props} />;
}
